import { Box, Typography, styled, Table, TableBody, TableCell, TableHead, TableRow } from '@mui/material'
import moment from 'moment'
import { FC } from 'react'

// components import
import { CardBox } from '../../theme/styleComponents'
import { images } from '../../assets/images'
import { flexJustifySpaceBetween } from '../../theme/styleConstants'
import { formatCamelCaseText } from '../../utils'
import { CustomTableCell } from '../common/CustomTableCell'
import { ReportPreviewData } from '../../interfaceTypes'
import {
  getReportingPeriod,
  getSelectionFieldEntries,
  isSpannedNotesRow,
  shouldHighlightLastRow
} from '../../utils/reports'

const CustomTable = styled(Table)(({ theme }) => ({
  minWidth: '600px',
  border: '1px solid #E0E0E0',
  '& .MuiTableCell-root': {
    padding: theme.spacing(1.5, 1),
    fontSize: '10px !important',
    '& .MuiTypography-root': {
      fontSize: '10px !important'
    }
  },
  '& .MuiTableHead-root .MuiTableCell-root': {
    position: 'sticky',
    top: 0,
    zIndex: 1
  },
  '@media print': {
    minWidth: 'auto',
    width: '100%',
    tableLayout: 'fixed',
    '& .MuiTableHead-root .MuiTableCell-root': {
      position: 'static'
    }
  }
}))

interface ReportPreviewProps {
  data: ReportPreviewData[]
  reportGroup: string
  reportSelection: string
  generatedDate: string
  selectionFields: Record<string, any>
  contentRef: React.RefObject<HTMLDivElement>
}

const ReportPreview: FC<ReportPreviewProps> = ({
  data,
  reportGroup,
  reportSelection,
  generatedDate,
  selectionFields,
  contentRef
}) => {
  const headers = data?.length > 0 ? Object.keys(data[0]) : []
  const reportingPeriond = getReportingPeriod(reportSelection, selectionFields) || generatedDate
  const highlightLastRow = shouldHighlightLastRow(reportSelection)
  const selectionEntries = getSelectionFieldEntries(
    Object.fromEntries(Object.entries(selectionFields).filter(([key]) => key !== 'matterId'))
  )

  if (!headers?.length) {
    return null
  }

  return (
    <CardBox
      id="report-preview"
      ref={contentRef}
      sx={{
        '@media print': {
          boxShadow: 'none !important',
          margin: '0 !important',
          width: '100% !important',
          minHeight: '100% !important',
          pageBreakAfter: 'always',
          '& *': {
            color: '#000 !important',
            fontFamily: 'inherit !important'
          }
        }
      }}
    >
      <Box
        sx={{
          px: 4,
          pt: 6,
          '@media print': {
            padding: '24px !important'
          }
        }}
      >
        <Box mb={5}>
          <Box sx={{ ...flexJustifySpaceBetween }}>
            <Box component="img" src={images.LOGO} alt="Helmer Legal Logo" sx={{ maxWidth: 120 }} />
            <Box textAlign="right">
              <Typography variant="h6" fontWeight={700} color="#0A2540">
                {formatCamelCaseText(reportSelection)}
              </Typography>
              <Typography variant="subtitle1" fontWeight={500} color="#0A2540">
                {moment(reportingPeriond).format('MMM D, YYYY')}
              </Typography>
            </Box>
          </Box>
          <Box sx={{ mt: { xs: 0.5, md: 1 }, display: 'inline-block', width: '100%' }}>
            <Box display="flex" flexDirection="column" sx={{ mt: { xs: 4, md: 0 }, width: { md: '30%', xs: '100%' } }}>
              <Box sx={{ ...flexJustifySpaceBetween }}>
                <Typography fontSize="12px">Report Group:</Typography>
                <Typography fontSize="12px">{formatCamelCaseText(reportGroup)}</Typography>
              </Box>
              <Box sx={{ ...flexJustifySpaceBetween }}>
                <Typography fontSize="12px">Report Selection:</Typography>
                <Typography fontSize="12px">{formatCamelCaseText(reportSelection)}</Typography>
              </Box>
              <Box sx={{ ...flexJustifySpaceBetween }}>
                <Typography fontSize="12px">Generated Date:</Typography>
                <Typography fontSize="12px">{moment(generatedDate).format('MMM D, YYYY hh:mm A')}</Typography>
              </Box>
              {selectionEntries.map(({ label, value }) => (
                <Box sx={{ ...flexJustifySpaceBetween }} key={label}>
                  <Typography fontSize="12px" color="text.secondary">{label}:</Typography>
                  <Typography fontSize="12px">{value}</Typography>
                </Box>
              ))}
            </Box>
          </Box>
        </Box>

        <Box
          mb={{ xs: 1.5, md: 3 }}
          sx={{
            maxHeight: '60vh',
            overflow: 'auto',
            '@media print': {
              maxHeight: 'none',
              overflow: 'visible !important',
              width: '100% !important'
            }
          }}
        >
          <CustomTable>
            <TableHead>
              <TableRow>
                {headers?.map((columnName, index) => (
                  <TableCell key={index} component="th" align={'left'}>
                    {columnName}
                  </TableCell>
                ))}
              </TableRow>
            </TableHead>
            <TableBody>
              {data?.map((row, rowIndex) =>
                isSpannedNotesRow(reportSelection, row) ? (
                  <TableRow key={rowIndex} hover>
                    <TableCell>
                      <Typography variant="body1" fontWeight={600}>
                        {Object.values(row)[0]?.toString()}
                      </Typography>
                    </TableCell>
                    <TableCell colSpan={headers.length - 1}>
                      <Typography variant="body1" sx={{ whiteSpace: 'pre-line' }}>
                        {Object.values(row)[1]?.toString()}
                      </Typography>
                    </TableCell>
                  </TableRow>
                ) : (
                  <TableRow key={rowIndex} selected={rowIndex === data.length - 1 && highlightLastRow} hover>
                    {Object.values(row)?.map((cellValue, cellIndex) => (
                      <CustomTableCell key={cellIndex} value={cellValue?.toString() || '-'} noWrap={false} />
                    ))}
                  </TableRow>
                )
              )}
            </TableBody>
          </CustomTable>
        </Box>
      </Box>

      <Box
        sx={{
          backgroundColor: '#F5F5F5',
          '& *': { fontSize: '12px !important' },
          '@media print': {
            backgroundColor: '#F5F5F5 !important',
            padding: '24px !important',
            marginTop: '24px !important',
            breakInside: 'avoid',
            pageBreakInside: 'avoid'
          }
        }}
        p={3}
        mt={3}
        display="inline-block"
        width="100%"
      >
        <Box width="50%" sx={{ float: 'left' }}></Box>
      </Box>
    </CardBox>
  )
}

export default ReportPreview
